import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

import { useStaleReason } from "./staleAppState";

/**
 * Blocking dialog shown when part of the app actually failed to load because
 * a newer deploy removed the chunks this tab references. Unlike the passive
 * sidebar indicator (version-poll), there is no way to dismiss this: the
 * broken action cannot succeed until the page picks up fresh assets.
 *
 * This is deliberately a separate dialog from ErrorModal, keyed on
 * staleAppState rather than the redux error slice:
 * - Most chunk failures never reach the error slice. React.lazy boundaries
 *   throw into the Sentry ErrorBoundary, and vite:preloadError / CSS preload
 *   failures dispatch nothing to redux. staleAppState is the one place every
 *   detection path converges (see deployVersionGuard).
 * - ErrorModal mounts inside the routed App tree. This dialog must also be
 *   able to appear when the route tree itself failed to load, so it is
 *   mounted in index.jsx, outside the router.
 *
 * For chunk errors that do land in the error slice, ErrorModal steps aside
 * and escalates to this dialog instead of rendering the raw message.
 */

const StaleReloadModal = () => {
  const staleReason = useStaleReason();

  if (staleReason !== "chunk-load-error") {
    return null;
  }

  return (
    <Dialog open fullWidth maxWidth="sm">
      <DialogTitle>App update required</DialogTitle>
      <DialogContent>
        <Typography className="text-base text-neutral-600">
          A new version of Ready to Activate was deployed while you had this tab
          open. Part of the app could not load. Reload to continue with the
          latest version.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={() => window.location.reload()}
          className="px-6 py-2 text-sm font-medium"
        >
          Reload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StaleReloadModal;
